import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  InputAdornment,
  Card,
  CardContent,
  Autocomplete,
} from '@mui/material';
import { Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { format } from 'date-fns';
import { api } from '../services/api';
import { Provider, ProviderCreate, ProviderUpdate } from '../types';
import ValidatedTextField from '../components/ValidatedTextField';
import AddressFields, { AddressValue } from '../components/AddressFields';
import ProviderVehiclesSection from '../components/ProviderVehiclesSection';
import { validatePassword } from '../utils/fieldValidation';
import { validateEmail, validatePhone, validateZipCode, validateState, validateNPI, validateContractNumber } from '../utils/fieldValidation';
import { fieldOnPageBlueSx } from '../mui/fieldOnPageBlue';

const Providers: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [formData, setFormData] = useState<ProviderCreate>({
    first_name: '',
    last_name: '',
    npi_number: '',
    contract_number: '',
    phone: '',
    email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    password: ''
  });
  
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [nameFilterProvider, setNameFilterProvider] = useState<Provider | null>(null);
  const [nameFilterInput, setNameFilterInput] = useState('');

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('Fetching providers...');
      const data = await api.providers.getAll();
      console.log('Providers data:', data);
      setProviders(data);
    } catch (err) {
      console.error('Error fetching providers:', err);
      setError(`Failed to load providers: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof ProviderCreate) => (value: string, isValid: boolean) => {
    setFormData({
      ...formData,
      [field]: value
    });
    
    setFieldErrors(prev => ({
      ...prev,
      [field]: !isValid
    }));
    
    // Check if all required fields are valid
    const requiredFields = ['first_name', 'last_name', 'email', 'contract_number'];
    const allRequiredValid = requiredFields.every(fieldName => {
      if (fieldName === field) return isValid;
      return !fieldErrors[fieldName] && formData[fieldName as keyof ProviderCreate];
    });
    
    // Check password confirmation for new providers
    const passwordMatch = isEditMode || formData.password === confirmPassword;
    
    setIsFormValid(allRequiredValid && passwordMatch);
  };

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setConfirmPassword(value);
    
    // Check if passwords match
    const passwordMatch = formData.password === value;
    setFieldErrors(prev => ({
      ...prev,
      confirmPassword: !passwordMatch
    }));
    
    // Update form validity
    const requiredFields = ['first_name', 'last_name', 'email', 'contract_number'];
    const allRequiredValid = requiredFields.every(fieldName => {
      return !fieldErrors[fieldName] && formData[fieldName as keyof ProviderCreate];
    });
    
    setIsFormValid(allRequiredValid && passwordMatch);
  };

  const handleAddNew = () => {
    setIsEditMode(false);
    setSelectedProvider(null);
    setFormData({
      first_name: '',
      last_name: '',
      npi_number: '',
      contract_number: '',
      phone: '',
      email: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: '',
      password: ''
    });
    setConfirmPassword('');
    setFieldErrors({});
    setIsFormValid(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsDialogOpen(true);
  };

  const handleEdit = (provider: Provider) => {
    setIsEditMode(true);
    setSelectedProvider(provider);
    setFormData({
      first_name: provider.first_name,
      last_name: provider.last_name,
      npi_number: provider.npi_number,
      contract_number: provider.contract_number || '',
      phone: provider.phone || '',
      email: provider.email,
      address_line1: provider.address_line1 || '',
      address_line2: provider.address_line2 || '',
      city: provider.city || '',
      state: provider.state || '',
      zip_code: provider.zip_code || '',
      password: '' // Don't show password in edit mode
    });
    setConfirmPassword('');
    setFieldErrors({});
    setIsFormValid(true); // Assume existing data is valid
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsDialogOpen(true);
  };

  const handleDelete = async (providerId: number | string) => {
    if (window.confirm('Are you sure you want to delete this provider?')) {
      try {
        await api.providers.delete(providerId);
        await fetchProviders();
      } catch (err) {
        console.error('Error deleting provider:', err);
        setError('Failed to delete provider. Please try again.');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Validate required fields
      if (!formData.first_name || !formData.last_name || !formData.email || !formData.npi_number) {
        setError('Please fill in all required fields: First Name, Last Name, Email, and NPI Number');
        return;
      }
      
      if (!isEditMode && !formData.password) {
        setError('Password is required for new providers');
        return;
      }
      
      if (!isEditMode && formData.password && formData.password.length < 8) {
        setError('Password must be at least 8 characters long');
        return;
      }
      
      if (isEditMode && selectedProvider) {
        // Prepare update data - include password only if it's provided
        const updateData: ProviderUpdate = { ...formData };
        // Only include password if it's been set (not empty)
        if (!updateData.password || updateData.password.trim() === '') {
          delete updateData.password;
        } else {
          // Validate password length if provided
          if (updateData.password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
          }
        }
        console.log('Updating provider with data:', updateData);
        await api.providers.update(selectedProvider.provider_id, updateData);
      } else {
        // Prepare data for creation - backend should handle password hashing
        const createData = {
          ...formData,
          // Ensure we're sending the password field as expected by the backend
          password: formData.password
        };
        console.log('Creating new provider with data:', createData);
        const result = await api.providers.create(createData);
        console.log('Provider creation result:', result);
      }
      
      setIsDialogOpen(false);
      await fetchProviders();
    } catch (err) {
      console.error('Error saving provider:', err);
      setError(`Failed to save provider: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    setIsDialogOpen(false);
    setSelectedProvider(null);
    setFormData({
      first_name: '',
      last_name: '',
      npi_number: '',
      contract_number: '',
      phone: '',
      email: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: '',
      password: ''
    });
    setConfirmPassword('');
    setFieldErrors({});
    setIsFormValid(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  const handleDialogBackdropClick = (event: object, reason: string) => {
    if (reason === 'backdropClick') {
      handleCloseDialog();
    }
  };

  const providerName = (provider: Provider) =>
    `${provider.first_name} ${provider.last_name}`.trim();

  const providerOptions = [...providers].sort((a, b) =>
    providerName(a).localeCompare(providerName(b), undefined, { sensitivity: 'base' })
  );

  const nameQuery = nameFilterInput.trim().toLowerCase();
  const visibleProviders = providers.filter((provider) => {
    if (nameFilterProvider) {
      return String(provider.provider_id) === String(nameFilterProvider.provider_id);
    }
    if (!nameQuery) return true;
    return providerName(provider).toLowerCase().includes(nameQuery);
  });

  const clearFilters = () => {
    setNameFilterProvider(null);
    setNameFilterInput('');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Providers Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add Provider
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Card sx={{ mb: 3, backgroundColor: '#ffffff' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Filter Providers
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={providerOptions}
                  value={nameFilterProvider}
                  inputValue={nameFilterInput}
                  onChange={(_, newValue) => setNameFilterProvider(newValue)}
                  onInputChange={(_, value, reason) => {
                    setNameFilterInput(value);
                    if (reason === 'input' || reason === 'clear') {
                      setNameFilterProvider(null);
                    }
                  }}
                  getOptionLabel={providerName}
                  isOptionEqualToValue={(option, value) =>
                    String(option.provider_id) === String(value.provider_id)
                  }
                  noOptionsText="No matching providers"
                  componentsProps={{ paper: { sx: { bgcolor: '#ffffff' } } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Provider"
                      placeholder="Type a name..."
                      sx={fieldOnPageBlueSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={clearFilters}
                  sx={{ mt: 1, backgroundColor: '#2D4B73', '&:hover': { backgroundColor: '#1E3452' } }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
      </Card>

      <Paper sx={{ p: 3 }}>
        {providers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No providers found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error ? error : 'Add your first provider to get started.'}
            </Typography>
          </Box>
        ) : visibleProviders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No providers match this search
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try a different name, or clear the filter to see every provider you can access.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Contract Number</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleProviders.map((provider) => (
                <TableRow key={provider.provider_id}>
                  <TableCell>
                    {provider.first_name} {provider.last_name}
                  </TableCell>
                  <TableCell>{provider.email}</TableCell>
                  <TableCell>{provider.contract_number || 'N/A'}</TableCell>
                  <TableCell>{provider.phone || 'N/A'}</TableCell>
                  <TableCell>
                    {provider.city && provider.state ? 
                      `${provider.city}, ${provider.state}` : 
                      'N/A'
                    }
                  </TableCell>
                  <TableCell>
                    {provider.created_at ? 
                      format(new Date(provider.created_at), 'MMM dd, yyyy') : 
                      'N/A'
                    }
                  </TableCell>
                  <TableCell>
                    {provider.updated_at ? 
                      format(new Date(provider.updated_at), 'MMM dd, yyyy') : 
                      'N/A'
                    }
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(provider)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(provider.provider_id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>

      {/* Add/Edit Provider Dialog */}
      <Dialog open={isDialogOpen} onClose={handleDialogBackdropClick} maxWidth="lg" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit Provider' : 'Add New Provider'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <ValidatedTextField
                entity="provider"
                field="first_name"
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={handleInputChange('first_name')}
                isRequired
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidatedTextField
                entity="provider"
                field="last_name"
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={handleInputChange('last_name')}
                isRequired
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidatedTextField
                entity="provider"
                field="email"
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                isRequired
                customValidation={validateEmail}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleInputChange('password')(e.target.value, validatePassword(e.target.value) === null)}
                required={!isEditMode}
                helperText={isEditMode ? "Leave blank to keep current password" : "Minimum 8 characters"}
                error={fieldErrors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {!isEditMode && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  required
                  error={fieldErrors.confirmPassword}
                  helperText={fieldErrors.confirmPassword ? "Passwords do not match" : "Confirm your password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <ValidatedTextField
                entity="provider"
                field="npi_number"
                fullWidth
                label="NPI Number"
                value={formData.npi_number}
                onChange={handleInputChange('npi_number')}
                customValidation={validateNPI}
                helperText="10-digit National Provider Identifier (optional)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidatedTextField
                entity="provider"
                field="contract_number"
                fullWidth
                label="Contract Number"
                value={formData.contract_number}
                onChange={handleInputChange('contract_number')}
                isRequired
                customValidation={validateContractNumber}
                helperText="7-digit contract number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ValidatedTextField
                entity="provider"
                field="phone"
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                customValidation={validatePhone}
              />
            </Grid>
            <Grid item xs={12}>
              <AddressFields
                value={{
                  address_line1: formData.address_line1 || '',
                  address_line2: formData.address_line2 || '',
                  city: formData.city || '',
                  state: formData.state || '',
                  zip_code: formData.zip_code || '',
                }}
                onChange={(next: AddressValue) => {
                  setFormData(prev => ({
                    ...prev,
                    address_line1: next.address_line1,
                    address_line2: next.address_line2,
                    city: next.city,
                    state: next.state,
                    zip_code: next.zip_code,
                  }));
                  // Keep zip/state field-error tracking roughly in sync for form validity
                  setFieldErrors(prev => ({
                    ...prev,
                    address_line1: false,
                    address_line2: false,
                    city: false,
                    state: validateState(next.state) !== null,
                    zip_code: validateZipCode(next.zip_code) !== null,
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12}>
              {isEditMode && selectedProvider?.provider_id != null ? (
                <ProviderVehiclesSection providerId={Number(selectedProvider.provider_id)} />
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Save this provider first, then edit them to add and manage vehicles.
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={isLoading || !isFormValid}
          >
            {isLoading ? <CircularProgress size={24} /> : (isEditMode ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onClose={handleCancelClose}>
        <DialogTitle>Abandon Provider?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to abandon this provider entry? All unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose}>Cancel</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Abandon
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Providers;
